import { useEffect, useState } from "react";
import { Navigate } from "react-router";

import { capture } from "@/lib/posthog";
import { useDepartments } from "@/hooks/queries/useDepartments";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { SegmentStep, type Segment } from "@/components/onboarding/SegmentStep";
import { useOnboardingSession } from "@/components/onboarding/useOnboardingSession";
import { SupplierFlow } from "@/components/onboarding/supplier/SupplierFlow";
import { ContractorFlow } from "@/components/onboarding/contractor/ContractorFlow";

/**
 * Onboarding de pantalla completa. Pregunta cómo va a usar LicitiGo y configura
 * su línea de negocio: el proveedor (le vende a quien gana la obra) y quien le
 * vende al Estado (obras, bienes o servicios) tienen cada uno su flujo. Cada
 * camino guarda la línea antes de su último paso. Se hace una sola vez: quien
 * ya lo vio y entra por la URL vuelve al dashboard.
 */
export default function Onboarding() {
  const session = useOnboardingSession();
  const { data: departments = [] } = useDepartments();

  const [segment, setSegment] = useState<Segment | null>(null);
  const [step, setStep] = useState<string>("segment");

  // ---- ANALÍTICA ----
  useEffect(() => {
    if (session.enteredSeen === false) capture("onboarding_opened");
  }, [session.enteredSeen]);

  useEffect(() => {
    if (session.enteredSeen === false) capture("onboarding_step_viewed", { segment, step });
  }, [segment, step, session.enteredSeen]);

  function selectSegment(next: Segment) {
    capture("onboarding_segment_selected", { segment: next });
    setSegment(next);
  }

  function backToSegments() {
    setSegment(null);
    setStep("segment");
  }

  if (session.enteredSeen === null) return null;
  if (session.enteredSeen) return <Navigate to="/dashboard" replace />;

  return (
    <OnboardingShell
      onClose={session.canLeave ? () => session.leave(segment, step) : undefined}
    >
      {segment === null && <SegmentStep onSelect={selectSegment} />}

      {segment === "supplier" && (
        <SupplierFlow
          session={session}
          departments={departments}
          onStepChange={setStep}
          onExit={backToSegments}
        />
      )}

      {segment === "contractor" && (
        <ContractorFlow
          session={session}
          departments={departments}
          onStepChange={setStep}
          onExit={backToSegments}
        />
      )}
    </OnboardingShell>
  );
}
